// ── Collateral Management — Availability ──
// Collateral availability, the revolving line's balance and excess
// availability, on one chart over time — for one borrower or the whole book.
//
// EVERY FIGURE IS THE CERTIFICATE'S OWN. Nothing is recomputed:
//   Collateral availability   gross_collateral_avail — what the collateral
//                             supports, before the commitment caps it
//   Commitment                loan_commitment
//   Borrowing base avail.     borrowing_base_avail — the lesser of the two
//   RLOC balance              total_after_requested (the balance once the
//                             requested funds go out), else current_loan_balance
//   Excess / (deficit)        excess_availability, as signed on the BBC
//
// ONE BORROWER: one point per certificate.
// ALL BORROWERS: one point per month. Each borrower contributes the last
// certificate dated in that month; a borrower with none that month carries its
// previous month's certificate forward ONE month (a mid-month reporter should
// not make the book dip and recover), and drops out after that. The tooltip
// and table say how many borrowers were carried. Excess is summed as signed, so
// one borrower's deficit nets against another's surplus — the deficit count is
// shown beside it so that netting cannot hide one.
//
// COMPARE WITH ACTUAL (optional, on by default). Beside the certificate's
// balance, the line's ACTUAL balance from the Portfolio app's records
// (api/getRlocBalanceHistory): the borrower's balance on every revolving line
// of the account, at the certificate's as-of date (one borrower) or the
// month-end (the book). And "excess vs actual" = the certificate's borrowing
// base availability less that actual balance. Where actual sits above the
// certificate's balance, the borrower was drawn past what the BBC showed.
import { useState, useEffect, useMemo, useId } from "react";
import { B } from "../../shared/theme";
import { fmtDate } from "../../shared/format";
import { Btn, Card, ErrorMsg, SectionHeader, Spinner, Td, TdNum } from "../../shared/ui";
import { useApp } from "../../shared/appContext";
import { useChartTheme, ChartThemeToggle, chartCardStyle, useWidth } from "../../shared/chartTheme";

const ALL = "__all__";
const num = v => { const n = parseFloat(v); return isFinite(n) ? n : null; };
const d10 = d => String(d || "").slice(0, 10);
const usd = v => (v == null ? "—" : (v < 0 ? "-$" : "$") + Math.round(Math.abs(v)).toLocaleString("en-US"));
const short = v => { const a = Math.abs(v); const s = a >= 1e9 ? (a / 1e9).toFixed(1) + "B" : a >= 1e6 ? (a / 1e6).toFixed(a >= 1e8 ? 0 : 1) + "M" : a >= 1e3 ? Math.round(a / 1e3) + "K" : String(Math.round(a)); return (v < 0 ? "-$" : "$") + s; };
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monLbl = ym => MON[+ym.slice(5, 7) - 1] + " " + ym.slice(2, 4);
const nextYm = ym => { let y = +ym.slice(0, 4), m = +ym.slice(5, 7) + 1; if (m > 12) { y++; m = 1; } return y + "-" + String(m).padStart(2, "0"); };

const balOf = r => (num(r.total_after_requested) != null ? num(r.total_after_requested) : num(r.current_loan_balance));
const pick = r => ({
  coll: num(r.gross_collateral_avail), commit: num(r.loan_commitment), bb: num(r.borrowing_base_avail),
  bal: balOf(r), excess: num(r.excess_availability),
});

// All borrowers → one point per month (see header).
function portfolioSeries(rows) {
  const by = new Map();                                   // accountid → Map(ym → last cert in month)
  rows.forEach(r => {
    const d = d10(r.asofdate); if (!d) return;
    const ym = d.slice(0, 7);
    if (!by.has(r.accountid)) by.set(r.accountid, new Map());
    const m = by.get(r.accountid), cur = m.get(ym);
    if (!cur || d > d10(cur.asofdate) || (d === d10(cur.asofdate) && Number(r.id) > Number(cur.id))) m.set(ym, r);
  });
  const yms = [...new Set(rows.map(r => d10(r.asofdate).slice(0, 7)).filter(Boolean))].sort();
  if (!yms.length) return [];
  const months = [];
  for (let ym = yms[0]; ym <= yms[yms.length - 1]; ym = nextYm(ym)) months.push(ym);
  const prevYm = {}; months.forEach((ym, i) => { prevYm[ym] = months[i - 1]; });
  return months.map(ym => {
    const p = { key: ym, label: monLbl(ym), date: ym, coll: 0, commit: 0, bb: 0, bal: 0, excess: 0, n: 0, carried: 0, deficits: 0,
                accts: [], monthEnd: new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7), 0)).toISOString().slice(0, 10) };
    by.forEach((m, accountid) => {
      let r = m.get(ym), carried = false;
      if (!r && prevYm[ym] && m.get(prevYm[ym])) { r = m.get(prevYm[ym]); carried = true; }
      if (!r) return;
      const v = pick(r);
      p.coll += v.coll || 0; p.commit += v.commit || 0; p.bb += v.bb || 0; p.bal += v.bal || 0; p.excess += v.excess || 0;
      p.n++; if (carried) p.carried++; if ((v.excess || 0) < 0) p.deficits++;
      p.accts.push({ accountid, name: r.companyname || String(accountid), bbid: r.id, date: d10(r.asofdate), carried, ...v });
    });
    return p;
  }).filter(p => p.n > 0);
}

const SERIES = [
  { key: "coll",   label: "Collateral availability" },
  { key: "commit", label: "Commitment" },
  { key: "bal",    label: "RLOC balance" },
  { key: "excess", label: "Excess / (deficit)" },
  { key: "actual", label: "Actual RLOC balance (Portfolio)", compare: true },
  { key: "excessAct", label: "Excess vs actual", compare: true },
];

// accountid → sorted [[date, cumulative balance]] from the movements.
const cumulate = hist => {
  const out = new Map();
  Object.entries(hist || {}).forEach(([a, mv]) => {
    let run = 0; out.set(String(a), mv.map(([d, x]) => { run += x; return [d, run]; }));
  });
  return out;
};
const balAt = (cum, accountid, date) => {
  const arr = cum.get(String(accountid));
  if (!arr) return null;                       // no revolving line on file
  let lo = 0, hi = arr.length - 1, ans = 0;
  while (lo <= hi) { const mid = (lo + hi) >> 1; if (arr[mid][0] <= date) { ans = arr[mid][1]; lo = mid + 1; } else hi = mid - 1; }
  return Math.max(0, Math.round(ans * 100) / 100);
};

// ── The chart ──────────────────────────────────────────────────────────────
const AvailChart = ({ pts, on, W, T, portfolio, onPick }) => {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [hover, setHover] = useState(null);
  if (!pts.length) return <div style={{ padding: "60px 0", textAlign: "center", color: T.muted, fontSize: 13 }}>No certificates in view.</div>;

  const C = {
    coll: T.accent, collFill: T.dark ? "#38d6f5" : B.cyan,
    commit: T.dark ? "rgba(255,255,255,0.55)" : "#8a8a85",
    bal: T.warm, pos: T.dark ? "#4fd1a5" : "#0F6E56", neg: T.dark ? "#ff7a6b" : "#993C1D",
    actual: T.dark ? "#c9a2ff" : "#6a4c93", excessAct: T.dark ? "#9cf0cf" : "#2f9e7a",
  };
  const H = 360, PADL = 70, PADR = 20, TOP = 18, BOT = 300, AXIS = 322;
  const vals = [];
  pts.forEach(p => {
    if (on.coll && p.coll != null) vals.push(p.coll);
    if (on.commit && p.commit != null) vals.push(p.commit);
    if (on.bal && p.bal != null) vals.push(p.bal);
    if (on.excess && p.excess != null) vals.push(p.excess);
    if (on.actual && p.actual != null) vals.push(p.actual);
    if (on.excessAct && p.excessAct != null) vals.push(p.excessAct);
  });
  const vMax = Math.max(1, ...vals) * 1.08;
  const vMin = Math.min(0, ...vals) * 1.15;
  const step = (W - PADL - PADR) / pts.length;
  const bw = Math.max(2, Math.min(22, step * 0.55));
  const cx = i => PADL + i * step + step / 2;
  const y = v => BOT - ((v - vMin) / (vMax - vMin)) * (BOT - TOP);
  const y0 = y(0);
  const path = k => {
    let d = "", pen = false;
    pts.forEach((p, i) => { const v = p[k]; if (v == null) { pen = false; return; } d += `${pen ? "L" : "M"}${cx(i).toFixed(1)},${y(v).toFixed(1)} `; pen = true; });
    return d.trim();
  };
  const collLine = path("coll");
  const firstColl = pts.findIndex(p => p.coll != null);
  let lastColl = -1; pts.forEach((p, i) => { if (p.coll != null) lastColl = i; });
  const collArea = firstColl >= 0 ? `${collLine} L${cx(lastColl).toFixed(1)},${y0.toFixed(1)} L${cx(firstColl).toFixed(1)},${y0.toFixed(1)} Z` : "";

  // y ticks
  const span = vMax - vMin, raw = span / 5, mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const stepT = [1, 2, 2.5, 5, 10].map(f => f * mag).find(s => s >= raw) || raw;
  const ticks = []; for (let t = Math.ceil(vMin / stepT) * stepT; t <= vMax; t += stepT) ticks.push(t);
  const every = Math.ceil(pts.length / Math.max(4, Math.floor((W - PADL - PADR) / 56)));
  const axisTxt = { fontSize: 10, fill: T.muted };
  const k = "av" + uid;
  const h = hover != null ? pts[hover] : null;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: "block", overflow: "visible" }}
           onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={`g${uid}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" style={{ stopColor: C.collFill, stopOpacity: T.dark ? 0.38 : 0.26 }} />
            <stop offset="100%" style={{ stopColor: C.collFill, stopOpacity: 0.02 }} />
          </linearGradient>
          <filter id={`f${uid}`} x="-10%" y="-30%" width="120%" height="160%">
            {T.dark
              ? <><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></>
              : <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#116588" floodOpacity="0.22" />}
          </filter>
        </defs>
        <style>{`
          .${k}-line{stroke-dasharray:1;stroke-dashoffset:1;animation:${k}-draw 1.4s ease-out forwards}
          .${k}-fade{opacity:0;animation:${k}-fd 1s .3s ease-out forwards}
          @keyframes ${k}-draw{to{stroke-dashoffset:0}}
          @keyframes ${k}-fd{to{opacity:1}}
          @media (prefers-reduced-motion: reduce){.${k}-line,.${k}-fade{animation:none;opacity:1;stroke-dashoffset:0}}
        `}</style>

        {ticks.map(t => (
          <g key={t}>
            <line x1={PADL} x2={W - PADR} y1={y(t)} y2={y(t)} stroke={t === 0 ? T.gridFirm : T.grid} strokeWidth={t === 0 ? 1 : 0.6} />
            <text x={PADL - 8} y={y(t) + 3} textAnchor="end" style={axisTxt}>{short(t)}</text>
          </g>
        ))}

        {/* excess bars, behind everything */}
        {on.excess && pts.map((p, i) => p.excess == null ? null : (
          <rect key={"e" + i} className={`${k}-fade`} x={cx(i) - bw / 2} width={bw} rx={2}
                y={Math.min(y(p.excess), y0)} height={Math.max(1, Math.abs(y(p.excess) - y0))}
                fill={p.excess >= 0 ? C.pos : C.neg} opacity={T.dark ? 0.75 : 0.6} />
        ))}

        {on.coll && collArea && <path className={`${k}-fade`} d={collArea} fill={`url(#g${uid})`} />}
        {on.coll && <path className={`${k}-line`} pathLength="1" d={collLine} fill="none" stroke={C.coll} strokeWidth={2.4} filter={`url(#f${uid})`} />}
        {on.commit && <path d={path("commit")} fill="none" stroke={C.commit} strokeWidth={1.4} strokeDasharray="5 4" />}
        {on.bal && <path className={`${k}-line`} pathLength="1" d={path("bal")} fill="none" stroke={C.bal} strokeWidth={2.4} filter={`url(#f${uid})`} />}
        {on.bal && pts.length <= 60 && pts.map((p, i) => p.bal == null ? null :
          <circle key={"b" + i} cx={cx(i)} cy={y(p.bal)} r={2.4} fill={C.bal} />)}
        {on.actual && <path d={path("actual")} fill="none" stroke={C.actual} strokeWidth={2} strokeDasharray="6 3" />}
        {on.actual && pts.length <= 60 && pts.map((p, i) => p.actual == null ? null :
          <rect key={"a" + i} x={cx(i) - 2.5} y={y(p.actual) - 2.5} width={5} height={5} fill={C.actual} />)}
        {on.excessAct && <path d={path("excessAct")} fill="none" stroke={C.excessAct} strokeWidth={1.6} strokeDasharray="2 3" />}
        {on.excessAct && pts.length <= 60 && pts.map((p, i) => p.excessAct == null ? null :
          <circle key={"x" + i} cx={cx(i)} cy={y(p.excessAct)} r={3} fill="none" stroke={p.excessAct < 0 ? C.neg : C.excessAct} strokeWidth={1.6} />)}

        {pts.map((p, i) => (i % every === 0 || i === pts.length - 1) && (
          <text key={"x" + i} x={cx(i)} y={AXIS} textAnchor="middle" style={axisTxt}>
            {portfolio ? p.label : fmtDate(p.date)}
          </text>
        ))}

        {h && <line x1={cx(hover)} x2={cx(hover)} y1={TOP} y2={BOT} stroke={T.gridFirm} strokeDasharray="3 3" />}
        {pts.map((p, i) => (
          <rect key={"h" + i} x={cx(i) - step / 2} width={step} y={TOP} height={BOT - TOP} fill="transparent"
                onMouseEnter={() => setHover(i)} onClick={() => onPick && onPick(p)}
                style={{ cursor: onPick ? "pointer" : "default" }} />
        ))}
      </svg>

      {h && (
        <div style={{ position: "absolute", top: 8, left: Math.min(Math.max(cx(hover) + 12, 0), W - 250), width: 238,
                      background: T.tipBg, border: "1px solid " + T.tipBorder, boxShadow: T.tipShadow, borderRadius: 8,
                      padding: "8px 10px", fontSize: 12, color: T.ink, pointerEvents: "none", lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{portfolio ? h.label : fmtDate(h.date)}</div>
          {[["Collateral availability", h.coll, C.coll], ["Commitment", h.commit, C.commit], ["Borrowing base avail.", h.bb, null],
            ["RLOC balance (BBC)", h.bal, C.bal], ["Excess / (deficit) (BBC)", h.excess, h.excess != null && h.excess < 0 ? C.neg : C.pos],
            ...(h.actual !== undefined ? [["Actual RLOC balance", h.actual, C.actual],
                                          ["Actual vs BBC balance", h.actual != null && h.bal != null ? h.actual - h.bal : null, null],
                                          ["Excess vs actual", h.excessAct, C.excessAct]] : [])].map(([l, v, c]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <span style={{ color: T.muted }}>{c && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: c, marginRight: 6 }} />}{l}</span>
              <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{usd(v)}</span>
            </div>
          ))}
          {h.bb > 0 && h.bal != null && <div style={{ color: T.muted }}>Utilization {(h.bal / h.bb * 100).toFixed(1)}% of borrowing base</div>}
          {portfolio && <div style={{ color: T.muted }}>{h.n} borrower{h.n === 1 ? "" : "s"}{h.carried ? ` · ${h.carried} carried from prior month` : ""}{h.deficits ? ` · ${h.deficits} in deficit` : ""}</div>}
          {h.actual !== undefined && <div style={{ color: T.muted }}>Actual balance at {portfolio ? fmtDate(h.monthEnd) : fmtDate(h.date)}{h.noRloc ? ` · ${h.noRloc} without a revolving line on file` : ""}</div>}
        </div>
      )}
    </div>
  );
};

// ── The page ───────────────────────────────────────────────────────────────
export const CollateralAvailability = () => {
  const { api, go, view, slim, openSlim } = useApp();
  const [open, setOpen] = useState({});                   // book view: months expanded to their borrowers
  // A certificate opens in its own window (one window, reused as you click
  // down the list); if the browser blocks it, the page navigates instead.
  const openCert = bbid => { if (!bbid) return; if (!slim && openSlim && openSlim("collateral-bbc", { bbid })) return; go("collateral-bbc", { bbid }); };
  const T = useChartTheme();
  const [ref, W] = useWidth(1000, 420);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [acct, setAcct] = useState(() => (view && view.accountid) || ALL);
  const [range, setRange] = useState("24");
  const [on, setOn] = useState({ coll: true, commit: true, bal: true, excess: true, actual: true, excessAct: false });
  const [compare, setCompare] = useState(true);
  const [actualHist, setActualHist] = useState(null);     // null = not loaded / unavailable
  const [actualErr, setActualErr] = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("getBorrowingBases")
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // The actual balances are extra: if they fail, the page still shows the BBCs.
  useEffect(() => {
    api.get("getRlocBalanceHistory").then(d => setActualHist(d || {})).catch(e => setActualErr(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const cum = useMemo(() => cumulate(actualHist), [actualHist]);
  const withActual = compare && actualHist != null;

  const borrowers = useMemo(() => {
    const m = new Map();
    rows.forEach(r => { if (r.accountid && !m.has(r.accountid)) m.set(r.accountid, r.companyname || String(r.accountid)); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const portfolio = acct === ALL;
  const all = useMemo(() => {
    if (portfolio) return portfolioSeries(rows);
    return rows.filter(r => String(r.accountid) === String(acct) && d10(r.asofdate))
      .slice().sort((a, b) => (d10(a.asofdate) === d10(b.asofdate) ? Number(a.id) - Number(b.id) : d10(a.asofdate) < d10(b.asofdate) ? -1 : 1))
      .map(r => ({ key: r.id, bbid: r.id, date: d10(r.asofdate), ...pick(r) }));
  }, [rows, acct, portfolio]);

  const pts = useMemo(() => {
    let out = all;
    if (range !== "all" && all.length) {
      const last = all[all.length - 1].date;
      const y = +last.slice(0, 4), m = +last.slice(5, 7) - (+range);
      const cut = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 7);
      out = all.filter(p => p.date.slice(0, 7) > cut);
    }
    if (!withActual) return out;
    return out.map(p => {
      if (!portfolio) {
        const a = balAt(cum, acct, p.date);
        return { ...p, actual: a, excessAct: a != null && p.bb != null ? p.bb - a : null };
      }
      let a = 0, bbKnown = 0, noRloc = 0;
      const accts = p.accts.map(x => {
        const v = balAt(cum, x.accountid, p.monthEnd);
        if (v == null) { noRloc++; return { ...x, actual: null, excessAct: null }; }
        a += v; bbKnown += x.bb || 0;
        return { ...x, actual: v, excessAct: x.bb != null ? x.bb - v : null };
      });
      const any = p.accts.length > noRloc;
      return { ...p, accts, actual: any ? a : null, excessAct: any ? bbKnown - a : null, noRloc };
    });
  }, [all, range, withActual, cum, portfolio, acct]);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const L = pts[pts.length - 1] || null;
  const util = L && L.bb > 0 && L.bal != null ? L.bal / L.bb : null;
  const tile = (label, v, note, color) => (
    <Card style={{ padding: "12px 14px", flex: "1 1 170px" }}>
      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: color || "inherit", marginTop: 2 }}>{v}</div>
      {note && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>{note}</div>}
    </Card>
  );
  const sel = { fontSize: 13, padding: "5px 8px", borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "#fff" };
  const legendColor = { coll: T.accent, commit: T.dark ? "rgba(255,255,255,0.55)" : "#8a8a85", bal: T.warm, excess: T.dark ? "#4fd1a5" : "#0F6E56",
                        actual: T.dark ? "#c9a2ff" : "#6a4c93", excessAct: T.dark ? "#9cf0cf" : "#2f9e7a" };
  const th = { fontSize: 12, fontWeight: 600, color: B.cyanDark, padding: "8px 12px", borderBottom: "2px solid " + B.cyan, position: "sticky", top: 0, background: "#fff", textAlign: "right" };

  return <>
    <SectionHeader title="Availability" actions={[
      <select key="a" value={acct} onChange={e => setAcct(e.target.value)} style={{ ...sel, maxWidth: 280 }}>
        <option value={ALL}>All borrowers (portfolio)</option>
        {borrowers.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
      </select>,
      <select key="r" value={range} onChange={e => setRange(e.target.value)} style={sel}>
        <option value="12">Last 12 months</option><option value="24">Last 24 months</option><option value="all">All history</option>
      </select>,
      !portfolio && <Btn key="d" onClick={() => go("collateral-dashboard", { accountid: acct })}>Borrower Dashboard</Btn>,
    ].filter(Boolean)} />

    {L && (
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        {tile("Collateral availability", usd(L.coll), portfolio ? L.label : "as of " + fmtDate(L.date))}
        {tile("Borrowing base avail.", usd(L.bb), "lesser of collateral and commitment " + usd(L.commit))}
        {tile("RLOC balance (BBC)", usd(L.bal), withActual && L.actual !== undefined
              ? `actual ${usd(L.actual)}${L.actual != null && L.bal != null ? ` (${L.actual - L.bal >= 0 ? "+" : ""}${usd(L.actual - L.bal)})` : ""}`
              : util != null ? (util * 100).toFixed(1) + "% of borrowing base" : null)}
        {tile("Excess / (deficit) (BBC)", usd(L.excess), portfolio && L.deficits ? `${L.deficits} borrower${L.deficits === 1 ? "" : "s"} in deficit` : null,
              L.excess != null && L.excess < 0 ? "#993C1D" : "#0F6E56")}
      </div>
    )}

    <Card style={{ marginBottom: 16, ...(chartCardStyle(T) || {}) }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.title, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Collateral availability vs RLOC balance
        </span>
        <span style={{ fontSize: 11, color: T.muted }}>{portfolio ? "book total, one point per month" : "one point per certificate"}</span>
        <label style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: T.muted, cursor: "pointer" }}
               title={actualErr ? "Actual balances could not be loaded: " + actualErr : "Add the line's actual balance from the Portfolio app"}>
          <input type="checkbox" checked={compare} disabled={!!actualErr} onChange={e => setCompare(e.target.checked)} />
          Compare with actual balance{actualErr ? " (unavailable)" : actualHist == null ? " (loading…)" : ""}
        </label>
        <div><ChartThemeToggle /></div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
        {SERIES.filter(s => !s.compare || withActual).map(s => (
          <button key={s.key} type="button" onClick={() => setOn(o => ({ ...o, [s.key]: !o[s.key] }))}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px", borderRadius: 12, cursor: "pointer", fontSize: 11,
                     border: "0.5px solid " + (T.dark ? "rgba(255,255,255,0.2)" : "var(--color-border-secondary)"),
                     background: "none", color: T.muted, opacity: on[s.key] ? 1 : 0.4 }}>
            <span style={{ width: 14, height: s.key === "excess" ? 10 : 3, borderRadius: 2, display: "inline-block",
                           background: ["commit", "actual", "excessAct"].includes(s.key) ? "none" : legendColor[s.key],
                           borderTop: ["commit", "actual", "excessAct"].includes(s.key) ? "2px " + (s.key === "excessAct" ? "dotted " : "dashed ") + legendColor[s.key] : "none" }} />
            {s.label}
          </button>
        ))}
      </div>
      <div ref={ref} style={{ width: "100%" }}>
        <AvailChart pts={pts} on={on} W={W} T={T} portfolio={portfolio}
          onPick={p => (portfolio ? setOpen(o => ({ ...o, [p.key]: true })) : openCert(p.bbid))} />
      </div>
    </Card>

    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ maxHeight: 420, overflowY: "auto", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
          <thead><tr>
            <th style={{ ...th, textAlign: "left" }}>{portfolio ? "Month" : "As-of"}</th>
            {portfolio && <th style={th}>Borrowers</th>}
            <th style={th}>Collateral avail.</th><th style={th}>Commitment</th><th style={th}>Borrowing base avail.</th>
            <th style={th}>RLOC balance (BBC)</th>
            {withActual && <><th style={th}>Actual RLOC bal.</th><th style={th}>Actual − BBC</th></>}
            <th style={th}>Utilization</th><th style={th}>Excess / (deficit) (BBC)</th>
            {withActual && <th style={th}>Excess vs actual</th>}
            {portfolio && <th style={th}>In deficit</th>}
          </tr></thead>
          <tbody>
            {pts.slice().reverse().map(p => {
              const isOpen = portfolio && open[p.key];
              const gap = x => (x.actual != null && x.bal != null ? x.actual - x.bal : null);
              const cells = (x, sub) => <>
                <TdNum>{usd(x.coll)}</TdNum><TdNum>{usd(x.commit)}</TdNum><TdNum>{usd(x.bb)}</TdNum>
                <TdNum>{usd(x.bal)}</TdNum>
                {withActual && <>
                  <TdNum title={portfolio && !sub ? `at ${fmtDate(p.monthEnd)}${p.noRloc ? ` · ${p.noRloc} borrower(s) without a revolving line on file` : ""}` : ""}>{usd(x.actual)}</TdNum>
                  <TdNum style={{ color: gap(x) != null && Math.abs(gap(x)) >= 1 ? (gap(x) > 0 ? "#993C1D" : "#0F6E56") : "var(--color-text-secondary)" }}>
                    {gap(x) != null ? (Math.abs(gap(x)) < 1 ? "—" : (gap(x) > 0 ? "+" : "") + usd(gap(x))) : "—"}</TdNum>
                </>}
                <TdNum>{x.bb > 0 && x.bal != null ? (x.bal / x.bb * 100).toFixed(1) + "%" : "—"}</TdNum>
                <TdNum style={{ fontWeight: sub ? 400 : 600, color: x.excess != null && x.excess < 0 ? "#993C1D" : "inherit" }}>{usd(x.excess)}</TdNum>
                {withActual && <TdNum style={{ fontWeight: sub ? 400 : 600, color: x.excessAct != null && x.excessAct < 0 ? "#993C1D" : "inherit" }}>{usd(x.excessAct)}</TdNum>}
              </>;
              return [
                <tr key={p.key} style={{ cursor: "pointer", background: isOpen ? B.cyanLight : "" }}
                    title={portfolio ? (isOpen ? "Hide the borrowers" : "Show each borrower behind this month") : "Open this certificate in its own window"}
                    onClick={() => (portfolio ? setOpen(o => ({ ...o, [p.key]: !o[p.key] })) : openCert(p.bbid))}>
                  <Td style={{ fontWeight: 500 }}>{portfolio ? <>{isOpen ? "▾ " : "▸ "}{p.label}</> : fmtDate(p.date)}</Td>
                  {portfolio && <TdNum title={p.carried ? `${p.carried} carried from the prior month` : ""}>{p.n}{p.carried ? <span style={{ color: "var(--color-text-secondary)" }}> ({p.carried} c/f)</span> : null}</TdNum>}
                  {cells(p, false)}
                  {portfolio && <TdNum style={{ color: p.deficits ? "#993C1D" : "inherit" }}>{p.deficits || "—"}</TdNum>}
                </tr>,
                ...(isOpen ? p.accts.slice().sort((a, b) => (a.excess ?? 0) - (b.excess ?? 0)).map(x => (
                  <tr key={p.key + "-" + x.accountid} style={{ cursor: "pointer", background: "#f6f8f9" }}
                      title={`Open ${x.name}'s ${fmtDate(x.date)} certificate in its own window`} onClick={() => openCert(x.bbid)}>
                    <Td style={{ paddingLeft: 28, fontSize: 12, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{x.name}</Td>
                    <TdNum style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{fmtDate(x.date)}{x.carried ? " c/f" : ""}</TdNum>
                    {cells(x, true)}
                    <TdNum style={{ fontSize: 12, color: "#993C1D", fontWeight: 600 }}>{x.excess != null && x.excess < 0 ? "Deficit" : ""}</TdNum>
                  </tr>
                )) : []),
              ];
            })}
          </tbody>
        </table>
      </div>
    </Card>
    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 10, lineHeight: 1.5 }}>
      Every figure is as signed on the certificate. RLOC balance is the loan balance after the requested funds (the
      certificate's current balance where that line is blank). {withActual
        ? `Actual RLOC balance is the borrower's balance on the account's revolving line(s) from the Portfolio app's records, at ${portfolio ? "each month-end" : "each certificate's as-of date"}; Actual − BBC above zero (red) means more was drawn than the certificate showed. Excess vs actual is the certificate's borrowing base availability less that actual balance. `
        : ""}{portfolio
        ? "The book total takes each borrower's last certificate in the month; a borrower with none that month carries its prior month forward once (c/f), then drops out. Excess is summed as signed, so deficits net against surpluses — In deficit counts the borrowers whose own certificate showed a deficit that month. Click a month (or a point on the chart) to see each borrower behind it, deficits first; click a borrower to open that certificate in its own window."
        : "Click a row, or a point on the chart, to open that certificate in its own window."}
    </div>
  </>;
};
